import { route } from 'preact-router';
import type { RoutableProps } from 'preact-router';
import { BookOpen, Sparkles, Feather, Shield, Lock, ArrowRight, Volume2, Palette } from 'lucide-preact';
import { getSavedStories } from '../lib/storage';
import { hasParentConsent } from '../lib/consent';

interface HomeProps extends RoutableProps {
  onStartCreate?: () => void;
}

export function Home({ onStartCreate }: HomeProps) {
  const savedStories = getSavedStories();
  const hasStories = savedStories.length > 0;
  const hasConsent = hasParentConsent();

  const handleCreateClick = () => {
    if (hasConsent) {
      route('/create');
    } else if (onStartCreate) {
      onStartCreate();
    }
  };

  const handleLibraryClick = () => {
    if (hasConsent) {
      route('/library');
    } else if (onStartCreate) {
      onStartCreate();
    }
  };

  return (
    <div className="min-h-screen gradient-warm overflow-hidden">
      {/* Decorative background elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 bg-gold/5 rounded-full blur-3xl float" />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-rust/5 rounded-full blur-3xl float"
          style={{ animationDelay: '2s' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-moss/3 rounded-full blur-3xl"
          style={{ animationDelay: '4s' }}
        />
      </div>

      <div className="relative container-custom py-16 sm:py-24 lg:py-32">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto">
          {/* Animated book icon */}
          <div
            className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-parchment border-ornate mb-10 float animate-in"
            style={{ animationDelay: '0.1s' }}
          >
            <BookOpen className="w-12 h-12 text-rust" strokeWidth={1.25} />
          </div>

          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-display font-medium text-ink mb-8 tracking-tight animate-in"
            style={{ animationDelay: '0.2s' }}
          >
            Once upon a time...
          </h1>

          <p
            className="text-xl sm:text-2xl text-ink/70 mb-12 leading-relaxed max-w-2xl mx-auto font-light animate-in"
            style={{ animationDelay: '0.3s' }}
          >
            Create magical stories where animal heroes embark on wondrous adventures. Each tale is
            uniquely crafted, just for you.
          </p>

          {/* CTA Buttons */}
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-in"
            style={{ animationDelay: '0.4s' }}
          >
            <button
              onClick={handleCreateClick}
              className="group relative px-8 py-4 bg-rust text-cream rounded-2xl font-medium text-lg
                       shadow-story hover:shadow-story-hover transition-all duration-300
                       hover:-translate-y-1 cursor-pointer flex items-center gap-3"
            >
              <Feather size={22} strokeWidth={1.5} className="group-hover:rotate-12 transition-transform" />
              Begin Your Story
              <ArrowRight
                size={18}
                className="opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all"
              />
            </button>

            {hasStories && (
              <button
                onClick={handleLibraryClick}
                className="group px-8 py-4 bg-parchment text-ink rounded-2xl font-medium text-lg
                         border-ornate hover:shadow-story transition-all duration-300
                         hover:-translate-y-1 cursor-pointer flex items-center gap-3"
              >
                <BookOpen size={20} strokeWidth={1.5} />
                My Stories
                <span className="px-2.5 py-0.5 text-sm bg-gold/20 text-rust rounded-full font-semibold">
                  {savedStories.length}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Feature Cards */}
        <div className="mt-24 lg:mt-32 max-w-4xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                step: '01',
                title: 'Choose',
                description: 'Select your animal hero and give them a name that speaks to their spirit',
                color: 'rust',
                delay: '0.5s',
              },
              {
                step: '02',
                title: 'Dream',
                description: 'Pick a magical world where your hero will discover their destiny',
                color: 'moss',
                delay: '0.6s',
              },
              {
                step: '03',
                title: 'Read',
                description: 'Watch as your unique story unfolds, ready to be shared or read aloud',
                color: 'night',
                delay: '0.7s',
              },
            ].map((item) => (
              <div
                key={item.step}
                className="group relative bg-cream/80 backdrop-blur-sm rounded-3xl p-8 border-ornate
                         card-hover animate-in"
                style={{ animationDelay: item.delay }}
              >
                {/* Step number */}
                <div
                  className={`font-display text-6xl font-light mb-4
                  ${item.color === 'rust' ? 'text-rust/20' : item.color === 'moss' ? 'text-moss/20' : 'text-night/20'}
                `}
                >
                  {item.step}
                </div>

                <h3 className="text-2xl font-display font-medium text-ink mb-3">{item.title}</h3>

                <p className="text-ink/60 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Demo Video Section */}
        <div
          className="mt-24 lg:mt-32 max-w-4xl mx-auto animate-in"
          style={{ animationDelay: '0.8s' }}
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-display font-medium text-ink mb-4">
              See the Magic
            </h2>
            <p className="text-lg text-ink/60">
              Watch how a personalized story comes to life
            </p>
          </div>

          {/* Demo video showing story reading experience with narration */}
          <div className="relative bg-parchment rounded-3xl border-ornate overflow-hidden aspect-video">
            <video
              className="w-full h-full object-cover"
              controls
              playsInline
              poster="/demo-poster.jpg"
            >
              <source src="/demo-final.webm" type="video/webm" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* Pricing Section */}
        <div
          className="mt-24 lg:mt-32 max-w-3xl mx-auto animate-in"
          style={{ animationDelay: '0.9s' }}
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-display font-medium text-ink mb-4">
              Simple Pricing
            </h2>
            <p className="text-lg text-ink/60">
              Each story is a keepsake worth treasuring
            </p>
          </div>

          <div className="bg-cream/80 backdrop-blur-sm rounded-3xl p-8 sm:p-10 border-ornate text-center">
            <div className="inline-flex items-baseline gap-1 mb-6">
              <span className="text-5xl sm:text-6xl font-display font-medium text-rust">$1.99</span>
              <span className="text-xl text-ink/50">per story</span>
            </div>

            <p className="text-ink/70 mb-8 max-w-md mx-auto">
              Create a personalized story starring your child's chosen animal hero.
              Professional narration and beautiful illustrations included.
            </p>

            <ul className="space-y-3 text-left max-w-sm mx-auto mb-8">
              {[
                { icon: BookOpen, text: 'Unique AI-generated story (300-400 words)' },
                { icon: Volume2, text: 'Professional voice narration' },
                { icon: Palette, text: 'Three custom illustrations' },
                { icon: Sparkles, text: 'Save, share, and read anytime' },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <item.icon size={20} className="text-rust flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span className="text-ink/70">{item.text}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={handleCreateClick}
              className="px-8 py-4 bg-rust text-cream rounded-2xl font-medium text-lg
                       shadow-story hover:shadow-story-hover transition-all duration-300
                       hover:-translate-y-1 cursor-pointer"
            >
              Create Your Story
            </button>

            <p className="mt-6 text-sm text-ink/40">
              No subscription required. Pay only for what you create.
            </p>
          </div>
        </div>

        {/* Trust Section */}
        <div
          className="mt-16 max-w-3xl mx-auto animate-in"
          style={{ animationDelay: '1.0s' }}
        >
          <div className="flex flex-wrap justify-center gap-8 lg:gap-12">
            {[
              { icon: Shield, text: 'Safe & Private' },
              { icon: Lock, text: 'No Account Needed' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-ink/50">
                <item.icon size={18} strokeWidth={1.5} />
                <span className="text-sm font-medium tracking-wide">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative border-t border-gold/10">
        <div className="container-custom py-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-ink/40">
              <a
                href="/privacy"
                className="hover:text-ink/70 transition-colors cursor-pointer"
              >
                Privacy
              </a>
              <a
                href="/terms"
                className="hover:text-ink/70 transition-colors cursor-pointer"
              >
                Terms
              </a>
              <a
                href="/settings"
                className="hover:text-ink/70 transition-colors cursor-pointer"
              >
                Settings
              </a>
            </div>
            <p className="text-sm text-ink/30 font-light italic">
              Made with care for young readers
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
